import { useState, useEffect, useMemo, useRef } from 'react'
import { apiService } from '../../services/api'
import { UserProfile } from '../../types'
import WalletPassUpdateButton from './WalletPassUpdateButton'
import UserDetailView from './UserDetailView'

interface CreateUserForm {
  email: string
  firstName: string
  lastName: string
  role: 'pilot' | 'photo_admin' | 'admin'
  emergencyContactName: string
  emergencyContactPhone: string
}

const EMPTY_FORM: CreateUserForm = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'pilot',
  emergencyContactName: '',
  emergencyContactPhone: '',
}

type SortField = 'firstName' | 'lastName' | 'email' | 'faiNation' | 'glider' | 'role' | 'participantNumber'
type SortDirection = 'asc' | 'desc'
type RoleFilter = 'all' | 'admin' | 'photo_admin' | 'pilot'

export default function UserManagement() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState<CreateUserForm>(EMPTY_FORM)
  const [creating, setCreating] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  const [sortField, setSortField] = useState<SortField>('firstName')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const containerRef = useRef<HTMLDivElement>(null)

  // Scroll to top of container when a user is selected
  useEffect(() => {
    if (selectedUser && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedUser])

  const filteredAndSortedUsers = useMemo(() => {
    let result = users

    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter((user) => user.role === roleFilter)
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter((user) =>
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      )
    }

    // Sort
    result = [...result].sort((a, b) => {
      let aVal: string | number = ''
      let bVal: string | number = ''

      switch (sortField) {
        case 'firstName':
          aVal = a.firstName.toLowerCase()
          bVal = b.firstName.toLowerCase()
          break
        case 'lastName':
          aVal = a.lastName.toLowerCase()
          bVal = b.lastName.toLowerCase()
          break
        case 'email':
          aVal = a.email.toLowerCase()
          bVal = b.email.toLowerCase()
          break
        case 'faiNation':
          aVal = (a.faiNation || '').toLowerCase()
          bVal = (b.faiNation || '').toLowerCase()
          break
        case 'glider':
          aVal = [a.paragliderManufacturer, a.paragliderModel].filter(Boolean).join(' ').toLowerCase()
          bVal = [b.paragliderManufacturer, b.paragliderModel].filter(Boolean).join(' ').toLowerCase()
          break
        case 'role':
          aVal = (a.role || 'pilot').toLowerCase()
          bVal = (b.role || 'pilot').toLowerCase()
          break
        case 'participantNumber':
          aVal = a.participantNumber || 9999
          bVal = b.participantNumber || 9999
          break
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return result
  }, [users, searchQuery, roleFilter, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300">↕</span>
    return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  useEffect(() => { loadUsers() }, [])

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    const response = await apiService.getAllUsers()
    if (response.error) {
      setError(response.error.message)
      setLoading(false)
      return
    }
    if (response.data) {
      const data = response.data as any
      const usersArray = data?.users || []
      setUsers(Array.isArray(usersArray) ? usersArray : [])
    } else {
      setUsers([])
    }
    setLoading(false)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    const userData: any = {
      email: createForm.email,
      firstName: createForm.firstName,
      lastName: createForm.lastName,
      role: createForm.role,
    }
    if (createForm.emergencyContactName || createForm.emergencyContactPhone) {
      userData.emergencyContact = {
        name: createForm.emergencyContactName,
        phone: createForm.emergencyContactPhone,
        relationship: 'Emergency Contact',
      }
    }
    const response = await apiService.createUser(userData)
    if (response.error) {
      setToast({ message: response.error.message, type: 'error' })
      setCreating(false)
      return
    }
    setToast({ message: 'User created successfully. Login credentials have been sent to their email.', type: 'success' })
    setShowCreateForm(false)
    setCreateForm(EMPTY_FORM)
    setCreating(false)
    loadUsers()
  }

  const handleDeleteUser = async (userId: string) => {
    const response = await apiService.deleteUser(userId)
    if (response.error) {
      setToast({ message: response.error.message, type: 'error' })
      return
    }
    setToast({ message: 'User deleted successfully', type: 'success' })
    setDeleteConfirm(null)
    loadUsers()
  }

  const handleDeleteAllUsers = async () => {
    const response = await apiService.deleteAllUsers()
    if (response.error) {
      setToast({ message: response.error.message, type: 'error' })
      return
    }
    setToast({ message: 'All users deleted successfully', type: 'success' })
    setShowDeleteAllConfirm(false)
    loadUsers()
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadUsers} className="mt-2 text-red-600 hover:text-red-800 font-medium">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add User
          </button>
          <button
            onClick={() => setShowDeleteAllConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete All Users
          </button>
        </div>
      </div>

      {selectedUser ? (
        <UserDetailView user={selectedUser} onClose={() => setSelectedUser(null)} />
      ) : (
      <>
      {/* Search bar and filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          aria-label="Filter by role"
        >
          <option value="all">All roles</option>
          <option value="admin">Admins</option>
          <option value="photo_admin">Photo Admins</option>
          <option value="pilot">Pilots</option>
        </select>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input id="create-email" type="email" required value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="pilot@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="create-firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input id="create-firstName" type="text" required value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label htmlFor="create-lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input id="create-lastName" type="text" required value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label htmlFor="create-role" className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select id="create-role" value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as CreateUserForm['role'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="pilot">Pilot</option>
                  <option value="photo_admin">Photo Admin</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <fieldset className="border border-gray-200 rounded-lg p-4">
                <legend className="text-sm font-medium text-gray-700 px-2">Emergency Contact (optional)</legend>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="create-ec-name" className="block text-sm text-gray-600 mb-1">Name</label>
                    <input id="create-ec-name" type="text" value={createForm.emergencyContactName}
                      onChange={(e) => setCreateForm({ ...createForm, emergencyContactName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                  <div>
                    <label htmlFor="create-ec-phone" className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input id="create-ec-phone" type="tel" value={createForm.emergencyContactPhone}
                      onChange={(e) => setCreateForm({ ...createForm, emergencyContactPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                </div>
              </fieldset>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreateForm(false); setCreateForm(EMPTY_FORM) }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={creating}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete All</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete all {users.length} users? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteAllConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteAllUsers}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('participantNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                  #<SortIcon field="participantNumber" />
                </th>
                <th onClick={() => handleSort('firstName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                  Name<SortIcon field="firstName" />
                </th>
                <th onClick={() => handleSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                  Email<SortIcon field="email" />
                </th>
                <th onClick={() => handleSort('faiNation')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                  FAI Nation<SortIcon field="faiNation" />
                </th>
                <th onClick={() => handleSort('glider')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                  Glider<SortIcon field="glider" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet Pass</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    {searchQuery || roleFilter !== 'all'
                      ? 'No users match your filters.'
                      : 'No users found. Add a user or import users to get started.'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.participantNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {user.firstName} {user.lastName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.faiNation || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {[user.paragliderManufacturer, user.paragliderModel].filter(Boolean).join(' ') || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.confirmationStatus === 'Confirmed' ? 'bg-green-100 text-green-800' :
                        user.confirmationStatus === 'Team pilot' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.confirmationStatus || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <WalletPassUpdateButton
                        userId={user.userId}
                        userName={`${user.firstName} ${user.lastName}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {deleteConfirm === user.userId ? (
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setDeleteConfirm(null)} className="text-gray-600 hover:text-gray-900">Cancel</button>
                          <button onClick={() => handleDeleteUser(user.userId)} className="text-red-600 hover:text-red-900">Confirm</button>
                        </div>
                      ) : (
                        <div className="flex gap-3 justify-end">
                          <button
                            onClick={async () => {
                              const response = await apiService.resendInvite(user.userId)
                              if (response.error) {
                                setToast({ message: response.error.message, type: 'error' })
                              } else {
                                setToast({ message: `Invite sent to ${user.email}`, type: 'success' })
                              }
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Invite
                          </button>
                          <button onClick={() => setDeleteConfirm(user.userId)} className="text-red-600 hover:text-red-900">
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-500">
        {searchQuery || roleFilter !== 'all'
          ? `Showing ${filteredAndSortedUsers.length} of ${users.length} users`
          : `Total users: ${users.length}`}
      </div>
      </>
      )}
    </div>
  )
}
